import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, Users, MapPin, X, Navigation, ImagePlus, RefreshCw, Trash2, Mic, Plus, Camera, ChevronLeft, ThumbsUp } from 'lucide-react';
import { markModuleSeen } from '@/lib/moduleBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [showMapsChoice, setShowMapsChoice] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [showPlusMenu, setShowPlusMenu] = useState(false); // menu "+" (photos, position)
  const [photoViewer, setPhotoViewer] = useState(null); // photo ouverte en plein ecran
  const [msgSheet, setMsgSheet] = useState(null); // id du message ouvert (like / vu par)
  const chatRootRef = useRef(null);
  const [kbOpen, setKbOpen] = useState(false); // clavier virtuel ouvert (mobile)
  const [deletingMessageId, setDeletingMessageId] = useState(null);

  const locale = language === 'ru' ? 'ru-RU' : language === 'md' ? 'ro-RO' : language === 'uk' ? 'uk-UA' : 'fr-FR';
  const isManager = user?.role === 'manager';
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const messagesContainerRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  // Ids des messages affiches, pour la passe meta du poll (likes / vu par /
  // suppressions) sans retelecharger le contenu
  const idsRef = useRef([]);

  const PAGE_SIZE = 15;

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      try {
        const [messagesRes, usersRes] = await Promise.all([
          axios.get(`${API}/chat/messages`, { params: { limit: PAGE_SIZE } }),
          axios.get(`${API}/users`)
        ]);

        if (!isMounted) return;

        const msgs = messagesRes.data || [];
        setMessages(msgs);
        setUsers(usersRes.data || []);
        setHasMore(msgs.length >= PAGE_SIZE);

        if (msgs.length > 0) {
          lastMessageIdRef.current = msgs[msgs.length - 1].id;
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
      
      if (isMounted) setLoading(false);
    };
    
    loadData();

    // La page est ouverte : les notifications de messages passent en lues (pastille effacee)
    markModuleSeen(['message']);

    // Poll toutes les 10 s, en deux passes legeres :
    // 1) les seuls NOUVEAUX messages (param after=), vide la plupart du temps ;
    // 2) une meta (id, likes, read_by) sur les messages affiches, qui met a
    //    jour les likes / vu par et retire les messages supprimes ailleurs.
    // L'ancien poll retelechargeait les 30 derniers messages en entier
    // (photos et vocaux compris) toutes les 10 s.
    const interval = setInterval(async () => {
      if (!isMounted) return;
      try {
        const params = lastMessageIdRef.current
          ? { after: lastMessageIdRef.current }
          : { limit: PAGE_SIZE };
        const res = await axios.get(`${API}/chat/messages`, { params });
        const fresh = (res.data || [])
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        if (!isMounted) return;
        if (fresh.length > 0) {
          // On est en train de lire le fil : ce qui arrive est vu immediatement
          markModuleSeen(['message']);
          lastMessageIdRef.current = fresh[fresh.length - 1].id;
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const toAdd = fresh.filter(m => !existingIds.has(m.id));
            return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
          });
        }

        const ids = idsRef.current;
        if (ids.length === 0) return;
        const metaRes = await axios.get(`${API}/chat/meta`, { params: { ids: ids.join(',') } });
        const metas = metaRes.data || [];
        if (!isMounted) return;
        const byId = new Map(metas.map(m => [m.id, m]));
        const queried = new Set(ids);
        setMessages(prev => {
          let changed = false;
          // Un id interroge absent de la reponse = message supprime ailleurs
          const kept = prev.filter(m => {
            if (queried.has(m.id) && !byId.has(m.id)) { changed = true; return false; }
            return true;
          });
          const merged = kept.map(m => {
            const f = byId.get(m.id);
            if (f && (
              (f.likes || []).join(',') !== (m.likes || []).join(',') ||
              (f.read_by || []).length !== (m.read_by || []).length
            )) {
              changed = true;
              return { ...m, likes: f.likes || [], read_by: f.read_by || [] };
            }
            return m;
          });
          if (!changed) return prev;
          // Meta seulement (likes / vu par) : ne pas sauter en bas du fil
          skipAutoScrollRef.current = true;
          return merged;
        });
      } catch (e) {
        // Ignore polling errors
      }
    }, 10000);
    
    return () => { 
      isMounted = false; 
      clearInterval(interval);
    };
  }, []);

  // Manual refresh function
  const refreshMessages = async () => {
    try {
      const response = await axios.get(`${API}/chat/messages`, { params: { limit: PAGE_SIZE } });
      const msgs = response.data || [];
      setMessages(msgs);
      setHasMore(msgs.length >= PAGE_SIZE);
      if (msgs.length > 0) lastMessageIdRef.current = msgs[msgs.length - 1].id;
      toast.success(t('updated'));
    } catch (error) {
      toast.error(t('error'));
    }
  };

  // Charger l'historique plus ancien (bouton en haut du fil), scroll preserve
  const loadOlderMessages = async () => {
    if (loadingOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const container = messagesContainerRef.current;
    const prevHeight = container ? container.scrollHeight : 0;
    const prevTop = container ? container.scrollTop : 0;
    try {
      const res = await axios.get(`${API}/chat/messages`, {
        params: { before: messages[0].id, limit: PAGE_SIZE }
      });
      const older = res.data || [];
      setHasMore(older.length >= PAGE_SIZE);
      if (older.length > 0) {
        skipAutoScrollRef.current = true;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          return [...older.filter(m => !existingIds.has(m.id)), ...prev];
        });
        // Le contenu ajoute au-dessus ne doit pas faire bouger ce qu'on lit
        requestAnimationFrame(() => {
          const c = messagesContainerRef.current;
          if (c) c.scrollTop = c.scrollHeight - prevHeight + prevTop;
        });
      }
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const skipAutoScrollRef = useRef(false);
  useEffect(() => {
    if (skipAutoScrollRef.current) {
      // Prepend d'historique : on ne saute pas en bas
      skipAutoScrollRef.current = false;
      return;
    }
    if (isInitialLoad && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom(true);
        setIsInitialLoad(false);
      }, 100);
    } else if (!isUserScrolling && messages.length > 0) {
      scrollToBottom();
    }
  }, [messages, isInitialLoad, isUserScrolling]);

  // Garde idsRef en phase avec les messages affiches (pour la passe meta du poll)
  useEffect(() => {
    idsRef.current = messages.map(m => m.id);
  }, [messages]);

  // Le champ d'ecriture grandit avec le texte (jusqu'a ~5 lignes), comme WhatsApp
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [newMessage]);

  // Clavier virtuel (iPhone/Android) : quand il s'ouvre, iOS fait defiler la page
  // et un grand vide apparaissait entre la barre d'ecriture et le clavier. On sort
  // donc le chat du flux (position fixed) et on le colle exactement sur le viewport
  // visuel, quoi que fasse iOS avec le scroll. L'overlay s'arme des le focus du
  // champ (une seule bascule nette, avant l'animation du clavier) pour eviter le
  // saut de texte au moment ou on clique pour ecrire.
  const kbArmedRef = useRef(false);
  const kbApplyRef = useRef(() => {});
  const kbGeomRef = useRef({ h: 0, t: 0 });
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let baseline = 0;
    let wasOverlay = false;
    const apply = () => {
      kbGeomRef.current = { h: vv.height, t: vv.offsetTop };
      const el = chatRootRef.current;
      if (!el) return;
      if (window.innerWidth >= 1024) {
        el.style.cssText = '';
        kbArmedRef.current = false;
        wasOverlay = false;
        setKbOpen(false);
        return;
      }
      baseline = Math.max(baseline, vv.height);
      const opened = kbArmedRef.current || vv.height < baseline - 150;
      setKbOpen(opened);
      if (opened) {
        el.style.position = 'fixed';
        el.style.top = '0';
        el.style.bottom = 'auto';
        el.style.left = '0';
        el.style.right = '0';
        el.style.zIndex = '60';
        el.style.margin = '0';
        el.style.height = `${vv.height}px`;
        // Un transform cree un containing block pour les overlays fixed internes
        // (visionneur photo, fond du menu +) : ne le poser que si necessaire
        el.style.transform = vv.offsetTop ? `translateY(${vv.offsetTop}px)` : '';
        wasOverlay = true;
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        });
      } else {
        el.style.cssText = '';
        if (wasOverlay) {
          wasOverlay = false;
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
          });
        }
      }
    };
    kbApplyRef.current = apply;
    const onOrientation = () => {
      baseline = 0;
      setTimeout(apply, 300);
    };
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    window.addEventListener('orientationchange', onOrientation);
    apply();
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      window.removeEventListener('orientationchange', onOrientation);
    };
  }, []);

  // iOS peut agrandir sa barre au-dessus du clavier EN COURS de frappe
  // (suggestions d'adresse / remplissage auto) sans emettre de resize du
  // visualViewport : le champ se retrouvait cache derriere et on ne voyait
  // plus ce qu'on tapait (Pavel 04/08). Tant que le clavier est ouvert, on
  // re-lit la geometrie a intervalle court et on ne re-applique que si elle
  // a vraiment change (pour ne pas re-coller le fil en bas pendant qu'on
  // remonte le lire).
  useEffect(() => {
    if (!kbOpen) return;
    const id = setInterval(() => {
      const vv = window.visualViewport;
      if (!vv) return;
      const g = kbGeomRef.current;
      if (Math.abs(vv.height - g.h) > 1 || Math.abs(vv.offsetTop - g.t) > 1) {
        kbApplyRef.current();
      }
    }, 250);
    return () => clearInterval(id);
  }, [kbOpen]);

  // Focus du champ : on bascule en mode clavier tout de suite (pas au milieu de
  // l'animation), le fil reste colle en bas. Au blur, retour normal des que le
  // clavier est referme.
  const handleInputFocus = () => {
    setShowPlusMenu(false);
    if (window.innerWidth < 1024) {
      kbArmedRef.current = true;
      kbApplyRef.current();
    }
  };
  const handleInputBlur = () => {
    kbArmedRef.current = false;
    setTimeout(() => kbApplyRef.current(), 60);
  };

  // Detect if user is scrolling up
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    setIsUserScrolling(!isAtBottom);
  };

  // --- Messages vocaux (Pavel 2026-08-02) ---
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recCancelledRef = useRef(false);
  const recSecondsRef = useRef(0);
  const recMimeRef = useRef('');

  const vTxt = (fr, ru, md, uk) => ({ fr, ru, md, uk }[language] || fr);

  const stopRecTimer = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null; }
  };

  useEffect(() => () => {
    // demontage : couper proprement micro et minuteur
    stopRecTimer();
    const mr = mediaRecorderRef.current;
    recCancelledRef.current = true;
    if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (e) {} }
  }, []);

  const sendVoice = async (blob, secs) => {
    setSending(true);
    try {
      const ext = (recMimeRef.current || '').includes('mp4') ? 'm4a' : 'webm';
      const fd = new FormData();
      fd.append('audio', blob, `voice.${ext}`);
      fd.append('duration', String(secs || 0));
      const response = await axios.post(`${API}/chat/send-voice`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, response.data]);
      lastMessageIdRef.current = response.data.id;
      setIsUserScrolling(false);
    } catch (error) {
      toast.error(t('messageSendError'));
    } finally {
      setSending(false);
    }
  };

  const startRecording = async () => {
    if (recording || sending) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = (window.MediaRecorder && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) ? 'audio/webm;codecs=opus'
        : ((window.MediaRecorder && MediaRecorder.isTypeSupported('audio/mp4')) ? 'audio/mp4' : '');
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recMimeRef.current = mr.mimeType || mime || 'audio/webm';
      recChunksRef.current = [];
      recCancelledRef.current = false;
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) recChunksRef.current.push(ev.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((tk) => tk.stop());
        stopRecTimer();
        if (!recCancelledRef.current && recChunksRef.current.length) {
          const blob = new Blob(recChunksRef.current, { type: recMimeRef.current });
          if (blob.size > 0) sendVoice(blob, recSecondsRef.current);
        }
        setRecording(false); setRecSeconds(0); recSecondsRef.current = 0;
      };
      mediaRecorderRef.current = mr;
      mr.start(250);
      setRecording(true); setRecSeconds(0); recSecondsRef.current = 0;
      recTimerRef.current = setInterval(() => {
        recSecondsRef.current += 1;
        setRecSeconds(recSecondsRef.current);
        if (recSecondsRef.current >= 120) stopRecording(false);   // 2 min maxi
      }, 1000);
    } catch (e) {
      toast.error(vTxt('Accès micro refusé. Autorise le micro pour envoyer un vocal.',
        'Доступ к микрофону запрещён. Разреши микрофон, чтобы отправить голосовое.',
        'Acces microfon refuzat. Permite microfonul pentru a trimite un mesaj vocal.',
        'Доступ до мікрофона заборонено. Дозволь мікрофон, щоб надіслати голосове.'));
    }
  };

  const stopRecording = (cancel) => {
    recCancelledRef.current = !!cancel;
    stopRecTimer();
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') { try { mr.stop(); } catch (e) { setRecording(false); } }
    else { setRecording(false); setRecSeconds(0); recSecondsRef.current = 0; }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/chat/send`, {
        content: newMessage.trim(),
        message_type: 'text'
      });
      setMessages(prev => [...prev, response.data]);
      // Update lastMessageIdRef to prevent duplicate fetch
      lastMessageIdRef.current = response.data.id;
      setNewMessage('');
      setIsUserScrolling(false); // Force scroll to bottom after sending
      inputRef.current?.focus();
    } catch (error) {
      toast.error(t('messageSendError'));
    } finally {
      setSending(false);
    }
  };

  // Handle multiple photos from gallery
  const handleGallerySelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    // Filter only images
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error(t('selectImages'));
      return;
    }
    
    // Limit to 5 photos max to avoid crashes
    const filesToProcess = imageFiles.slice(0, 5);
    if (imageFiles.length > 5) {
      toast.info(t('maxPhotos'));
    }
    
    setSending(true);

    let successCount = 0;
    let skippedTooBig = 0;

    // Process one at a time to avoid memory issues
    for (const file of filesToProcess) {
      if (file.size > 10 * 1024 * 1024) { skippedTooBig += 1; continue; } // Skip files > 10MB
      
      try {
        const compressedDataUrl = await compressImage(file);
        const blob = await fetch(compressedDataUrl).then(r => r.blob());
        
        const formData = new FormData();
        formData.append('photo', blob, 'photo.jpg');
        formData.append('caption', '');
        
        const response = await axios.post(`${API}/chat/send-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setMessages(prev => [...prev, response.data]);
        lastMessageIdRef.current = response.data.id;
        successCount++;
        
        // Small delay between uploads to prevent overload
        if (filesToProcess.length > 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch (error) {
        console.error('Failed to send photo:', error);
      }
    }
    
    setSending(false);
    
    if (skippedTooBig > 0) {
      toast.info(vTxt(
        `${skippedTooBig} photo(s) trop lourde(s) ignorée(s) (10 Mo max)`,
        `${skippedTooBig} фото слишком большие, пропущены (макс. 10 МБ)`,
        `${skippedTooBig} poze prea mari, ignorate (max 10 MB)`,
        `${skippedTooBig} фото занадто великі, пропущені (макс. 10 МБ)`
      ));
    }
    if (successCount > 0) {
      setIsUserScrolling(false);
      toast.success(
        successCount === 1
          ? t('photoSent')
          : `${successCount} ${t('photosSent')}`
      );
    } else if (skippedTooBig === 0) {
      toast.error(t('sendError'));
    }

    // Reset file input
    if (e.target) e.target.value = '';
  };

  // Compress image function - optimized for mobile
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Resize to max 800px for chat (much smaller for faster loading)
            const maxSize = 800;
            if (width > maxSize || height > maxSize) {
              if (width > height) {
                height = (height / width) * maxSize;
                width = maxSize;
              } else {
                width = (width / height) * maxSize;
                height = maxSize;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to JPEG with 50% quality for maximum compression
            const compressedData = canvas.toDataURL('image/jpeg', 0.5);
            resolve(compressedData);
          } catch (error) {
            console.error('Compression error:', error);
            reject(error);
          }
        };
        img.onerror = (error) => {
          console.error('Image load error:', error);
          reject(error);
        };
        img.src = e.target.result;
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  const sendLocation = async () => {
    if (!locationAddress.trim()) {
      toast.error(t('enterAddress'));
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/chat/send`, {
        content: locationAddress,
        message_type: 'location',
        location_name: locationName || t('addressForMaterials'),
        location_address: locationAddress
      });
      setMessages(prev => [...prev, response.data]);
      lastMessageIdRef.current = response.data.id;
      setShowLocationDialog(false);
      setLocationName('');
      setLocationAddress('');
      setIsUserScrolling(false); // Force scroll to bottom after sending
      toast.success(t('addressSent'));
    } catch (error) {
      toast.error(t('messageSendError'));
    } finally {
      setSending(false);
    }
  };

  // Delete message (manager only)
  const handleDeleteMessage = async (messageId) => {
    if (!isManager) return;
    
    const confirmMsg = { 
      fr: 'Voulez-vous vraiment supprimer ce message ?', 
      ru: 'Вы уверены, что хотите удалить это сообщение?', 
      md: 'Sigur doriți să ștergeți acest mesaj?',
      uk: 'Ви впевнені, що хочете видалити це повідомлення?'
    }[language] || 'Voulez-vous vraiment supprimer ce message ?';
    
    if (!window.confirm(confirmMsg)) return;
    
    setDeletingMessageId(messageId);
    try {
      await axios.delete(`${API}/chat/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success(t('messageDeleted'));
    } catch (error) {
      toast.error(t('deleteError'));
    } finally {
      setDeletingMessageId(null);
    }
  };

  const openGoogleMaps = (address) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  const openWaze = (address) => {
    const url = `https://waze.com/ul?q=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  const handleLocationClick = (address) => {
    setSelectedAddress(address);
    setShowMapsChoice(true);
  };

  // Like / retire le like d'un message (optimiste, corrige par la reponse serveur)
  const toggleLike = async (msg) => {
    const uid = user?.id;
    if (!uid) return;
    const wasLiked = (msg.likes || []).includes(uid);
    skipAutoScrollRef.current = true;
    setMessages(prev => prev.map(m => m.id === msg.id
      ? { ...m, likes: wasLiked ? (m.likes || []).filter(i => i !== uid) : [...(m.likes || []), uid] }
      : m));
    try {
      const r = await axios.post(`${API}/chat/messages/${msg.id}/like`);
      const likes = r.data?.likes || [];
      skipAutoScrollRef.current = true;
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, likes } : m));
    } catch (e) {
      skipAutoScrollRef.current = true;
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, likes: msg.likes || [] } : m));
    }
  };

  const scrollToBottom = (instant = false) => {
    if (instant) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();

    // Reset hours to compare just the dates
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateDay.getTime() === today.getTime()) {
      return t('today');
    } else if (dateDay.getTime() === yesterday.getTime()) {
      return t('yesterday');
    } else {
      return date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
    }
  };

  const groupMessagesByDate = (msgs) => {
    const groups = {};
    msgs.forEach(msg => {
      const dateKey = new Date(msg.created_at).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = { date: msg.created_at, messages: [] };
      }
      groups[dateKey].messages.push(msg);
    });
    return Object.values(groups);
  };

  const onlineUsers = users.filter(u => u.id !== user?.id);

  if (loading) {
    return (
      <div className="chat-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const messageGroups = groupMessagesByDate(messages);
  const sheetMsg = msgSheet ? messages.find(m => m.id === msgSheet) : null;
  const sheetLiked = sheetMsg ? (sheetMsg.likes || []).includes(user?.id) : false;
  const userById = (id) => users.find(u => u.id === id) || (user?.id === id ? user : null);
  const sheetLikers = sheetMsg ? (sheetMsg.likes || []).map(userById).filter(Boolean) : [];
  const sheetSeers = sheetMsg
    ? (sheetMsg.read_by || []).filter(id => id !== sheetMsg.sender_id).map(userById).filter(Boolean)
    : [];
  const groupTitle = t('groupChat');
  const teamLabel = t('wholeTeam');
  const photoLabel = t('sendPhoto');
  const locationLabel = t('sendAddress');
  const locationNameLabel = t('locationName');
  const addressLabel = t('address');
  const openMapsLabel = t('openInMaps');
  
  return (
    <div ref={chatRootRef} data-testid="chat-page" className="chat-screen -m-4 md:-m-6 lg:-m-8 flex flex-col">
      {/* Header */}
      <div
        className="p-4 text-white flex items-center gap-3 sm:gap-4 shadow-md"
        style={{
          backgroundColor: 'var(--color-primary)',
          // En mode clavier le chat couvre tout l'ecran : le header absorbe l'encoche
          paddingTop: kbOpen ? 'calc(0.65rem + var(--safe-area-inset-top))' : undefined,
          paddingBottom: kbOpen ? '0.65rem' : undefined
        }}
      >
        {/* Retour (mobile) : la barre du bas est masquee dans Messages */}
        <button
          onClick={() => navigate('/')}
          className="lg:hidden -ml-1 p-1.5 rounded-full hover:bg-white/20 transition-colors"
          title={{ fr: 'Retour', ru: 'Назад', md: 'Înapoi', uk: 'Назад' }[language] || 'Retour'}
          data-testid="chat-back-btn"
        >
          <ChevronLeft className="w-7 h-7 text-white" />
        </button>
        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-secondary)' }}>
          <Users className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-white">{groupTitle}</h1>
          <p className="text-[13px] text-white/70">{teamLabel} • {onlineUsers.length + 1} {t('members')}</p>
        </div>
        {/* Refresh button */}
        <button
          onClick={refreshMessages}
          className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
          title={t('refresh')}
        >
          <RefreshCw className="w-5 h-5 text-white" />
        </button>
        <div className="hidden sm:flex items-center -space-x-2">
          {onlineUsers.slice(0, 5).map((u) => (
            <div 
              key={u.id} 
              className="w-8 h-8 rounded-full border-2 overflow-hidden"
              style={{ borderColor: 'var(--color-primary)', backgroundColor: 'var(--color-secondary)' }}
              title={u.name}
            >
              {u.photo_url ? (
                <img src={u.photo_url} alt={u.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-white font-bold">
                  {u.name.charAt(0)}
                </div>
              )}
            </div>
          ))}
          {onlineUsers.length > 5 && (
            <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs text-white font-bold"
              style={{ borderColor: 'var(--color-primary)', backgroundColor: 'var(--color-secondary)' }}
            >
              +{onlineUsers.length - 5}
            </div>
          )}
        </div>
      </div>
      
      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-3 py-2"
        style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          backgroundColor: '#f1f5f9'
        }}
      >
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center bg-white/90 backdrop-blur p-8 rounded-xl shadow-sm">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(14,33,71,0.1)' }}>
                <Users className="w-10 h-10" style={{ color: 'var(--color-primary)' }} />
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-primary)' }}>{groupTitle}</h2>
              <p className="text-slate-500">{t('startConversation')}</p>
            </div>
          </div>
        ) : (
          <>
          {hasMore && (
            <div className="flex justify-center pt-1 pb-2">
              <button
                type="button"
                onClick={loadOlderMessages}
                disabled={loadingOlder}
                className="bg-white/90 backdrop-blur text-slate-500 text-xs font-medium px-4 py-1.5 rounded-full shadow-sm hover:text-slate-700 transition-colors disabled:opacity-60"
                data-testid="load-older-btn"
              >
                {loadingOlder
                  ? vTxt('Chargement...', 'Загрузка...', 'Se încarcă...', 'Завантаження...')
                  : vTxt('Messages précédents', 'Предыдущие сообщения', 'Mesaje anterioare', 'Попередні повідомлення')}
              </button>
            </div>
          )}
          {messageGroups.map((group, groupIndex) => (
            <div key={groupIndex}>
              <div className="flex justify-center my-3">
                <span className="bg-white/90 backdrop-blur text-slate-600 text-xs font-medium px-3 py-1 rounded-full shadow-sm">
                  {formatDateHeader(group.date)}
                </span>
              </div>
              {group.messages.map((msg) => {
                const isMe = msg.sender_id === user?.id;
                // L'avatar vient de la fiche utilisateur (les messages ne le
                // transportent plus) ; sender_photo reste en repli
                const senderAvatar = users.find(u => u.id === msg.sender_id)?.photo_url || msg.sender_photo;
                return (
                  <div
                    key={msg.id}
                    className={`flex mb-2 group ${isMe ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isMe && (
                      <div className="w-8 h-8 rounded-full bg-slate-300 overflow-hidden mr-2 flex-shrink-0 self-end">
                        {senderAvatar ? (
                          <img src={senderAvatar} alt={msg.sender_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-xs font-bold text-slate-600">
                            {msg.sender_name.charAt(0)}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Delete button for manager (left side for own messages) */}
                    {isManager && isMe && (
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        disabled={deletingMessageId === msg.id}
                        className="opacity-30 active:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity self-center mr-2 p-1.5 rounded-full hover:bg-red-100 text-red-500"
                        title={t('deleteMessage')}
                        data-testid={`delete-message-${msg.id}`}
                      >
                        {deletingMessageId === msg.id ? (
                          <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    <div
                      onClick={() => setMsgSheet(msg.id)}
                      className={`max-w-[75%] rounded-2xl shadow-sm overflow-hidden cursor-pointer ${
                        isMe
                          ? 'text-white rounded-br-md'
                          : 'bg-white text-slate-900 rounded-bl-md border border-slate-100'
                      }`}
                      style={isMe ? { backgroundColor: 'var(--color-primary)' } : {}}
                      data-testid={`msg-bubble-${msg.id}`}
                    >
                      {/* Photo : grande et cliquable (plein ecran) */}
                      {msg.message_type === 'photo' && msg.photo_data && (
                        <img
                          src={msg.photo_data}
                          alt="Photo"
                          loading="lazy"
                          decoding="async"
                          onClick={(e) => { e.stopPropagation(); setPhotoViewer({ url: msg.photo_data, name: msg.sender_name }); }}
                          className="block w-60 sm:w-72 max-w-full max-h-80 object-cover cursor-pointer active:opacity-80"
                          data-testid={`chat-photo-${msg.id}`}
                        />
                      )}

                      {/* Voice message */}
                      {msg.message_type === 'voice' && msg.audio_data && (
                        <div className="px-2.5 pt-2.5 min-w-[230px] max-w-[280px]" onClick={(e) => e.stopPropagation()}>
                          <audio controls preload="metadata" src={msg.audio_data} className="w-full h-10" />
                        </div>
                      )}

                      {/* Adresse : ligne fine, tape = ouvrir dans Maps */}
                      {msg.message_type === 'location' && (
                        <div
                          className={`px-3 py-2 cursor-pointer flex items-center gap-2.5 ${isMe ? 'bg-white/10' : 'bg-[#0E2147]/5'}`}
                          onClick={(e) => { e.stopPropagation(); handleLocationClick(msg.location_address || msg.content); }}
                        >
                          <MapPin className={`w-5 h-5 flex-shrink-0 ${isMe ? 'text-white' : 'text-[#0E2147]'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{msg.location_name || vTxt('Adresse', 'Адрес', 'Adresă', 'Адреса')}</p>
                            <p className={`text-xs truncate ${isMe ? 'text-white/70' : 'text-slate-500'}`}>{msg.location_address || msg.content}</p>
                          </div>
                          <Navigation className={`w-4 h-4 flex-shrink-0 ${isMe ? 'text-white/80' : 'text-[#0E2147]'}`} />
                        </div>
                      )}

                      {/* Text content */}
                      <div className="px-3 py-1.5">
                        {!isMe && (
                          <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--color-primary)' }}>{msg.sender_name}</p>
                        )}
                        {(msg.message_type === 'text' || msg.content) && msg.message_type !== 'location' && (
                          <p className="text-[15px] leading-[1.4] break-words whitespace-pre-line">{msg.content}</p>
                        )}
                        <p className={`text-[10px] mt-0.5 text-right tabular-nums ${isMe ? 'text-white/70' : 'text-slate-400'}`}>
                          {(msg.likes || []).length > 0 && (
                            <span className="mr-1.5" data-testid={`likes-count-${msg.id}`}>
                              <ThumbsUp className="w-3 h-3 inline -mt-0.5 mr-0.5 fill-current" strokeWidth={1.5} /> {(msg.likes || []).length}
                            </span>
                          )}
                          {formatMessageTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                    {/* Delete button for manager (right side for other's messages) */}
                    {isManager && !isMe && (
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        disabled={deletingMessageId === msg.id}
                        className="opacity-30 active:opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity self-center ml-2 p-1.5 rounded-full hover:bg-red-100 text-red-500"
                        title={t('deleteMessage')}
                        data-testid={`delete-message-${msg.id}`}
                      >
                        {deletingMessageId === msg.id ? (
                          <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input - disposition WhatsApp (Pavel 2026-08-02) : + | champ | camera | micro rond */}
      <div className="px-3 pt-2 bg-gray-100 border-t relative" style={{ borderColor: 'var(--color-border)', paddingBottom: kbOpen ? '0.5rem' : 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}>
        {/* Menu du bouton + : photos et position */}
        {showPlusMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowPlusMenu(false)} />
            <div className="absolute bottom-full mb-2 left-3 z-50 w-60 bg-white rounded-2xl shadow-xl border overflow-hidden animate-slideUp">
              <button
                type="button"
                onClick={() => { setShowPlusMenu(false); galleryInputRef.current?.click(); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left"
                data-testid="plus-menu-photos"
              >
                <span className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center">
                  <ImagePlus className="w-5 h-5 text-purple-600" />
                </span>
                <span className="font-medium text-sm text-slate-700">{photoLabel}</span>
              </button>
              <button
                type="button"
                onClick={() => { setShowPlusMenu(false); setShowLocationDialog(true); }}
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left border-t"
                data-testid="plus-menu-location"
              >
                <span className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                </span>
                <span className="font-medium text-sm text-slate-700">{locationLabel}</span>
              </button>
            </div>
          </>
        )}
        
        <form onSubmit={sendMessage} className="flex items-end gap-2">
          {recording ? (
            <>
              {/* Annuler l'enregistrement */}
              <button
                type="button"
                onClick={() => stopRecording(true)}
                className="p-2 text-slate-500 hover:bg-red-50 hover:text-red-500 rounded-full transition-colors"
                title={vTxt('Annuler', 'Отмена', 'Anulează', 'Скасувати')}
                data-testid="voice-cancel-btn"
              >
                <X className="w-6 h-6" />
              </button>
              <div className="flex-1 flex items-center gap-3 bg-white rounded-full h-12 px-4 shadow-sm">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></span>
                <span className="font-mono text-sm text-slate-700">
                  {Math.floor(recSeconds / 60)}:{String(recSeconds % 60).padStart(2, '0')}
                </span>
                <span className="text-xs text-slate-400 flex-1 text-right truncate">
                  {vTxt('Enregistrement...', 'Запись...', 'Înregistrare...', 'Запис...')}
                </span>
              </div>
              {/* Terminer et envoyer */}
              <Button
                type="button"
                onClick={() => stopRecording(false)}
                className="w-12 h-12 rounded-full text-white p-0 shadow-md transition-transform hover:scale-105"
                style={{ backgroundColor: 'var(--color-primary)' }}
                data-testid="voice-send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </>
          ) : (
          <>
          {/* Bouton + : regroupe photos et position (disposition WhatsApp) */}
          <button
            type="button"
            onClick={() => setShowPlusMenu(!showPlusMenu)}
            disabled={sending}
            className={`p-2 rounded-full transition-all flex-shrink-0 ${showPlusMenu ? 'bg-[#0E2147]/15 text-[#0E2147] rotate-45' : 'text-slate-600 hover:bg-[#0E2147]/10'}`}
            title={vTxt('Joindre', 'Прикрепить', 'Atașează', 'Прикріпити')}
            data-testid="plus-menu-btn"
          >
            <Plus className="w-7 h-7" strokeWidth={1.8} />
          </button>

          {/* Entrees fichiers cachees : galerie (multiple) + prise de vue directe */}
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleGallerySelect}
            className="hidden"
            disabled={sending}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleGallerySelect}
            className="hidden"
            disabled={sending}
          />

          {/* Champ arrondi qui s'agrandit avec le texte */}
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={(e) => {
                // Entree envoie sur ordinateur seulement; sur telephone la touche
                // retour fait un saut de ligne (sinon impossible d'ecrire sur 2 lignes)
                if (e.key === 'Enter' && !e.shiftKey && window.innerWidth >= 1024) {
                  e.preventDefault();
                  if (newMessage.trim() && !sending) sendMessage(e);
                }
              }}
              placeholder={{ fr: 'Message', ru: 'Сообщение', md: 'Mesaj', uk: 'Повідомлення' }[language] || 'Message'}
              autoComplete="off"
              rows={1}
              className="w-full block bg-white border-0 rounded-3xl shadow-sm px-4 py-3 text-base md:text-sm leading-snug resize-none focus:outline-none focus:ring-2"
              style={{ '--tw-ring-color': 'var(--color-primary)', minHeight: '48px', maxHeight: '132px', overflowY: 'auto' }}
              data-testid="message-input"
            />
          </div>

          {/* Appareil photo : prise de vue directe */}
          <button
            type="button"
            onClick={() => { setShowPlusMenu(false); cameraInputRef.current?.click(); }}
            disabled={sending}
            className="p-2 text-slate-600 hover:bg-[#0E2147]/10 rounded-full transition-colors disabled:opacity-50 flex-shrink-0"
            title={photoLabel}
            data-testid="camera-btn"
          >
            <Camera className="w-7 h-7" strokeWidth={1.8} />
          </button>

          {/* Bouton rond : micro quand le champ est vide, envoi des qu'on tape */}
          {newMessage.trim() ? (
            <Button
              type="submit"
              disabled={sending}
              className="w-12 h-12 rounded-full text-white p-0 shadow-md transition-transform hover:scale-105 flex-shrink-0"
              style={{ backgroundColor: 'var(--color-primary)' }}
              data-testid="send-message-btn"
            >
              {sending ? <div className="spinner w-5 h-5" /> : <Send className="w-5 h-5" />}
            </Button>
          ) : (
            <button
              type="button"
              onClick={startRecording}
              disabled={sending}
              className="w-12 h-12 rounded-full text-white shadow-md flex items-center justify-center transition-transform hover:scale-105 disabled:opacity-50 flex-shrink-0"
              style={{ backgroundColor: 'var(--color-primary)' }}
              title={vTxt('Message vocal', 'Голосовое сообщение', 'Mesaj vocal', 'Голосове повідомлення')}
              data-testid="voice-record-btn"
            >
              {sending ? <div className="spinner w-5 h-5" /> : <Mic className="w-6 h-6" />}
            </button>
          )}
          </>
          )}
        </form>
      </div>

      {/* Feuille d'actions sur un message : like + vu par (portail : au-dessus de tout) */}
      {sheetMsg && createPortal(
        <div
          className="fixed inset-0 z-[9000] bg-black/40 flex items-end lg:items-center lg:justify-center"
          onClick={() => setMsgSheet(null)}
          data-testid="msg-sheet"
        >
          <div
            className="bg-white w-full lg:w-96 rounded-t-2xl lg:rounded-2xl px-4 pt-3 pb-6 max-h-[70vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-3 lg:hidden" />
            <button
              onClick={() => toggleLike(sheetMsg)}
              className="w-full flex items-center gap-3 py-2.5 px-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="sheet-like-btn"
            >
              <ThumbsUp className={`w-6 h-6 ${sheetLiked ? 'text-blue-600 fill-blue-600' : 'text-slate-500'}`} />
              <span className="font-medium text-slate-800">
                {sheetLiked
                  ? vTxt("Je n'aime plus", 'Убрать лайк', 'Nu îmi mai place', 'Прибрати вподобання')
                  : vTxt("J'aime", 'Нравится', 'Îmi place', 'Подобається')}
              </span>
            </button>
            {sheetLikers.length > 0 && (
              <div className="mt-1 px-2">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                  <ThumbsUp className="w-3 h-3 inline -mt-0.5 mr-1 fill-current" strokeWidth={1.5} /> {sheetLikers.length}
                </p>
                {sheetLikers.map(u => (
                  <div key={u.id} className="flex items-center gap-2.5 py-1">
                    <div className="w-7 h-7 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                      {u.photo_url
                        ? <img src={u.photo_url} alt={u.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[11px] font-bold text-slate-600">{(u.name || '?').charAt(0)}</div>}
                    </div>
                    <span className="text-sm text-slate-700">{u.name}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-slate-100 px-2">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                {vTxt('Vu par', 'Просмотрели', 'Văzut de', 'Переглянули')}
              </p>
              {sheetSeers.length === 0 ? (
                <p className="text-sm text-slate-400 py-1">
                  {vTxt('Personne pour le moment', 'Пока никто', 'Nimeni deocamdată', 'Поки ніхто')}
                </p>
              ) : (
                sheetSeers.map(u => (
                  <div key={u.id} className="flex items-center gap-2.5 py-1">
                    <div className="w-7 h-7 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                      {u.photo_url
                        ? <img src={u.photo_url} alt={u.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[11px] font-bold text-slate-600">{(u.name || '?').charAt(0)}</div>}
                    </div>
                    <span className="text-sm text-slate-700">{u.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Photo en plein ecran (tape sur une photo du fil) : portail aussi */}
      {photoViewer && createPortal(
        <div
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-[99999] p-3"
          onClick={() => setPhotoViewer(null)}
          data-testid="chat-photo-viewer"
        >
          <button onClick={() => setPhotoViewer(null)} className="absolute top-4 right-4 p-2 text-white bg-white/10 rounded-full">
            <X className="w-7 h-7" />
          </button>
          <img src={photoViewer.url} alt={photoViewer.name || 'Photo'} className="max-w-full max-h-[88vh] object-contain rounded-xl" />
          {photoViewer.name && (
            <p className="absolute bottom-6 left-0 right-0 text-center text-white/80 text-sm">{photoViewer.name}</p>
          )}
        </div>,
        document.body
      )}

      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {locationLabel}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center">
              <MapPin className="w-8 h-8 text-blue-600" />
            </div>
            
            <div className="form-group">
              <Label className="form-label">{locationNameLabel}</Label>
              <Input
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder={t('addressForMaterials')}
                className="input-base"
              />
            </div>
            
            <div className="form-group">
              <Label className="form-label">{addressLabel} *</Label>
              <Input
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
                placeholder={{ fr: '123 Rue Exemple, Ville', ru: '123 ул. Примерная, Город', md: '123 Strada Exemplu, Oraș', uk: '123 вул. Приклад, Місто' }[language] || '123 Rue Exemple, Ville'}
                className="input-base"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowLocationDialog(false);
                  setLocationName('');
                  setLocationAddress('');
                }}
                variant="outline"
                className="btn-secondary flex-1"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={sendLocation}
                disabled={!locationAddress.trim() || sending}
                className="btn-primary flex-1"
              >
                {sending ? <div className="spinner w-4 h-4" /> : <Navigation className="w-4 h-4 mr-2" />}
                {t('send')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Maps Choice Dialog */}
      <Dialog open={showMapsChoice} onOpenChange={setShowMapsChoice}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center" style={{ color: 'var(--color-primary)' }}>
              {{ fr: 'Ouvrir avec', ru: 'Открыть с помощью', md: 'Deschide cu', uk: 'Відкрити за допомогою' }[language] || 'Ouvrir avec'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <button
              onClick={() => {
                openGoogleMaps(selectedAddress);
                setShowMapsChoice(false);
              }}
              className="w-full p-4 flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-[#0E2147] hover:bg-[#0E2147]/5 transition-all"
              data-testid="google-maps-btn"
            >
              <div className="w-12 h-12 rounded-full bg-[#4285F4] flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-lg" style={{ color: 'var(--color-primary)' }}>Google Maps</p>
                <p className="text-sm text-gray-500">
                  {{ fr: 'Navigation Google', ru: 'Навигация Google', md: 'Navigare Google', uk: 'Навігація Google' }[language] || 'Navigation Google'}
                </p>
              </div>
            </button>
            
            <button
              onClick={() => {
                openWaze(selectedAddress);
                setShowMapsChoice(false);
              }}
              className="w-full p-4 flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-[#0E2147] hover:bg-[#0E2147]/5 transition-all"
              data-testid="waze-btn"
            >
              <div className="w-12 h-12 rounded-full bg-[#33CCFF] flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-white" fill="currentColor">
                  <path d="M20.54 6.63c.69.94 1.15 2.04 1.35 3.19.21 1.21.12 2.45-.25 3.63-.38 1.22-1.02 2.34-1.88 3.27-.54.58-1.14 1.09-1.79 1.53-.23.15-.46.3-.7.43-.42.24-.86.45-1.31.63-.19.07-.38.15-.58.21-.89.28-1.81.43-2.74.43-.65 0-1.29-.06-1.92-.18-.68-.13-1.34-.33-1.97-.6-.1-.04-.2-.09-.31-.13-.51-.22-1-.48-1.46-.79-.08-.05-.15-.1-.23-.15-.85-.58-1.59-1.3-2.2-2.12-.37-.49-.68-1.02-.94-1.58-.08-.17-.15-.34-.22-.52-.29-.74-.48-1.52-.56-2.31-.02-.2-.03-.39-.04-.59 0-.06 0-.13-.01-.19v-.28c.01-.64.1-1.28.25-1.9.17-.69.42-1.35.74-1.98.11-.22.23-.43.36-.64.44-.71.97-1.35 1.58-1.91.17-.16.35-.31.53-.45C7.54 3.88 8.33 3.4 9.17 3c.32-.15.65-.28 1-.39.72-.23 1.47-.37 2.24-.41.35-.02.69-.02 1.04 0 .48.03.95.1 1.42.21.53.12 1.04.29 1.53.51.08.04.17.07.25.11.5.22.98.49 1.42.8.08.06.16.11.24.17.71.52 1.33 1.14 1.85 1.84.12.16.24.33.35.5l.03.29zm-8.56 9.1c.41 0 .74-.34.74-.75 0-.41-.33-.75-.74-.75-.41 0-.75.34-.75.75 0 .41.34.75.75.75zm-3.5 0c.41 0 .75-.34.75-.75 0-.41-.34-.75-.75-.75-.41 0-.74.34-.74.75 0 .41.33.75.74.75zm5.25-4.5c0 .97-.79 1.75-1.75 1.75s-1.75-.78-1.75-1.75.79-1.75 1.75-1.75 1.75.78 1.75 1.75z"/>
                </svg>
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-lg" style={{ color: 'var(--color-primary)' }}>Waze</p>
                <p className="text-sm text-gray-500">
                  {{ fr: 'Navigation Waze', ru: 'Навигация Waze', md: 'Navigare Waze', uk: 'Навігація Waze' }[language] || 'Navigation Waze'}
                </p>
              </div>
            </button>
          </div>
          
          <Button
            onClick={() => setShowMapsChoice(false)}
            variant="outline"
            className="w-full btn-secondary"
          >
            {t('cancel')}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
